/**
 * OrgChangePicker — Step 1 of OrgChangeStepper (v0.14 Wave 3)
 *
 * Tenant-scoped search picker that returns both client (organization) and
 * DBA (organization_establishment) results in a single union'd list per
 * §5.0 query. DBAs visually distinguished with parent-client context line
 * per §5.8.
 *
 * Spec sections covered:
 *   §1.0 — Step 1 stepper panel
 *   §5.0 — Search query (orgs + DBAs, alphabetical, LIMIT 50, archived filtered)
 *   §5.1 — 0-results state with always-visible Create CTA
 *   §5.2 — "Type more to narrow" when results hit LIMIT
 *   §5.8 — DBA visual distinction with parent-client suffix
 *   §5.11 — Empty initial state shows all clients
 *   §11.4 — Loading skeleton
 *   §11.11 — Keyboard accessibility
 *
 * The "+ Create new client" CTA is ALWAYS visible regardless of search state
 * — Christa's footgun: she scans, picks the first visible action.
 */
import { useEffect, useMemo, useState } from 'react';
import { Search, Plus, Building2, Building, Loader2, Undo2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useOrgChangeSearch } from '@/hooks';
import type { OrgChangeSearchResult } from '@/lib/schemas';
import { getPreviousClient } from './orgChangePreviousClient';

export interface OrgChangePickerProps {
  searchValue: string;
  onSearchChange: (v: string) => void;
  /** Current invoice's organization_id — used to flag the "current client"
   *  row so user knows what they're switching from. */
  currentOrgId?: string | null;
  /** Polish wave 2 — B2 (2026-05-30): invoice id is the storage key for
   *  the "Previously selected — use to revert" affordance per §5.12.
   *  When the picker opens on an invoice that had a client switch in the
   *  last 1 hour, the previous client is rendered as a synthetic top row
   *  with a revert caption. Optional — when omitted, the affordance is
   *  silently skipped (preserves the existing call sites that pass props
   *  by name rather than spread). */
  invoiceId?: string;
  onSelect: (result: OrgChangeSearchResult) => void;
  onCreateNew: () => void;
}

/** Debounce helper. 250ms per spec. */
function useDebouncedValue<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

export function OrgChangePicker({
  searchValue,
  onSearchChange,
  currentOrgId,
  invoiceId,
  onSelect,
  onCreateNew,
}: OrgChangePickerProps) {
  const debouncedQuery = useDebouncedValue(searchValue, 250);

  // §5.11: empty-query state — show all clients alphabetical. Backend stub
  // returns alphabetical first 50 on empty `q`. Until Wave 2 ships, the hook
  // sends q='' which our search endpoint should treat as "list all."
  // To keep network traffic sane in the meantime, only fire when q has 1+
  // chars; the empty-state UI just shows the Create CTA.
  const { data, isLoading, isError } = useOrgChangeSearch(debouncedQuery, {
    enabled: debouncedQuery.trim().length > 0,
  });

  const results = data?.results ?? [];
  const hitLimit = results.length >= 50;

  // Polish wave 2 — B2 (2026-05-30): §5.12 "Previously selected — use to
  // revert." Read the prior client (if any) on every render so that:
  //   - Initial open (no search yet) surfaces it at the top.
  //   - It carries through to the searched-results view as a synthetic
  //     top row regardless of whether the search query matches its name.
  // Re-reads on every render are cheap (one sessionStorage hit), and
  // useMemo would only obscure the lifecycle (entry can expire mid-session
  // — recomputing makes the expiry honest). We DO suppress it when the
  // currentOrgId equals the previous (defensive — should never happen
  // since we only record on a real switch, but better quiet than wrong).
  const previousClient = useMemo(() => {
    if (!invoiceId) return null;
    const entry = getPreviousClient(invoiceId);
    if (!entry) return null;
    if (currentOrgId && String(currentOrgId) === entry.previousOrgId) return null;
    return entry;
    // currentOrgId is the only outside value besides invoiceId; refresh
    // when either changes. We intentionally do NOT depend on a "time"
    // tick — getPreviousClient handles TTL on read.
  }, [invoiceId, currentOrgId]);

  return (
    <div className="space-y-3" data-testid="org-change-picker">
      <p className="text-sm text-slate-600">
        Search for an existing client or DBA. Or create a brand new client below.
      </p>

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
        <Input
          autoFocus
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search clients and DBAs..."
          className="pl-8"
          data-testid="org-change-picker-search"
          aria-label="Search clients and DBAs"
        />
      </div>

      {/* Always-visible Create CTA (§5.0c equivalent / Christa footgun #1) */}
      <button
        type="button"
        onClick={onCreateNew}
        className="w-full flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-md text-sm text-blue-700 hover:bg-blue-50 hover:border-blue-400 transition-colors"
        data-testid="org-change-picker-create-new"
      >
        <Plus className="w-4 h-4" />
        <span className="font-medium">Create new client</span>
        {searchValue.trim() && (
          <span className="text-slate-500 truncate">
            &mdash; "{searchValue.trim()}"
          </span>
        )}
      </button>

      {/* Results list */}
      <div
        className="border border-slate-200 rounded-md max-h-72 overflow-y-auto"
        data-testid="org-change-picker-results"
      >
        {/* Polish wave 2 — B2 (2026-05-30): §5.12 "Previously selected —
            use to revert." Synthetic row pinned to the top whenever the
            invoice had a client switch in the last 1 hour. Clicking it
            re-runs the org-change flow with the prior client as the
            target — Christa's misclick recovery, no super-admin needed.
            Renders REGARDLESS of search query so the affordance can't
            slip below the fold during a quick "I switched it wrong" panic. */}
        {previousClient && (
          <button
            type="button"
            onClick={() =>
              onSelect({
                kind: 'org',
                id: previousClient.previousOrgId,
                name: previousClient.previousOrgName,
                legal_name: previousClient.previousOrgName,
                parent_org_id: null,
                parent_org_name: null,
                is_archived: false,
              })
            }
            className="w-full text-left px-3 py-2 hover:bg-amber-50 transition-colors flex items-start gap-2 bg-amber-50/40 border-b border-amber-100"
            data-testid="org-change-picker-revert-previous"
          >
            <span className="mt-0.5 flex-shrink-0 text-amber-700">
              <Undo2 className="w-4 h-4" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-900 truncate">
                  {previousClient.previousOrgName}
                </span>
                <span
                  className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                  data-testid="org-change-picker-revert-badge"
                >
                  Revert
                </span>
              </span>
              <span className="block text-xs text-slate-600 mt-0.5">
                Previously selected &mdash; use to revert
              </span>
            </span>
          </button>
        )}

        {debouncedQuery.trim().length === 0 && !previousClient && (
          <div
            className="p-4 text-sm text-slate-500 italic text-center"
            data-testid="org-change-picker-empty-prompt"
          >
            Start typing to find a client or DBA.
          </div>
        )}
        {debouncedQuery.trim().length === 0 && previousClient && (
          <div
            className="p-3 text-xs text-slate-500 italic text-center border-t border-slate-100"
            data-testid="org-change-picker-empty-prompt-with-revert"
          >
            Or start typing to find a different client or DBA.
          </div>
        )}

        {debouncedQuery.trim().length > 0 && isLoading && (
          <div
            className="p-4 text-sm text-slate-500 flex items-center justify-center gap-2"
            data-testid="org-change-picker-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Searching...
          </div>
        )}

        {debouncedQuery.trim().length > 0 && isError && (
          <div
            className="p-4 text-sm text-amber-700 bg-amber-50"
            data-testid="org-change-picker-error"
          >
            Couldn&apos;t search right now. Please try again.
          </div>
        )}

        {debouncedQuery.trim().length > 0 &&
          !isLoading &&
          !isError &&
          results.length === 0 && (
            <div
              className="p-4 text-sm text-slate-500 text-center"
              data-testid="org-change-picker-no-results"
            >
              No matches for &quot;{debouncedQuery.trim()}&quot;. Create a new
              client above.
            </div>
          )}

        {results.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {results.map((r) => {
              const isCurrent =
                r.kind === 'org' && currentOrgId && String(r.id) === currentOrgId;
              return (
                <li key={`${r.kind}-${r.id}`}>
                  <button
                    type="button"
                    onClick={() => onSelect(r)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors flex items-start gap-2"
                    data-testid={`org-change-picker-result-${r.kind}-${r.id}`}
                  >
                    <span className="mt-0.5 flex-shrink-0">
                      {r.kind === 'org' ? (
                        <Building className="w-4 h-4 text-blue-700" />
                      ) : (
                        <Building2 className="w-4 h-4 text-amber-600" />
                      )}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900 truncate">
                          {r.name}
                        </span>
                        {r.kind === 'dba' && (
                          <span
                            className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                            title="Doing Business As — a brand under a parent client"
                          >
                            DBA
                          </span>
                        )}
                        {isCurrent && (
                          <span
                            className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200"
                            data-testid={`org-change-picker-result-${r.kind}-${r.id}-current`}
                          >
                            Current
                          </span>
                        )}
                      </span>
                      {/* §5.8: DBA shows parent-client context. */}
                      {r.kind === 'dba' && r.parent_org_name && (
                        <span className="block text-xs text-slate-600 mt-0.5">
                          DBA under{' '}
                          <span className="font-medium">{r.parent_org_name}</span>
                        </span>
                      )}
                      {r.kind === 'org' && r.legal_name && r.legal_name !== r.name && (
                        <span className="block text-xs text-slate-500 mt-0.5 truncate">
                          {r.legal_name}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {hitLimit && (
          <div
            className="p-2 text-xs text-slate-500 border-t border-slate-100 text-center"
            data-testid="org-change-picker-hit-limit"
          >
            Showing the first 50 matches. Type more to narrow.
          </div>
        )}
      </div>
    </div>
  );
}
